import { Button } from '@/src/shadComponents/ui/button';
import { Divider } from '@/src/tailwindui/divider';
import { Subheading } from '@/src/tailwindui/heading';
import TemplateGrid from './TemplateGrid';
import { cn } from '@/src/lib/utils';
import { DefinitionTemplate } from './hooks/useCreateEvent';
import ExperienceCustomizer from './ExperienceCustomizer';
import { useCallback, useEffect } from 'react';
import {
    processExperienceTemplate,
    processFlowTemplate,
} from '@/src/lib/templateUtils';

export default function CreateEventStepTwo({
    platform,
    experience,
    setExperience,
    experienceTemplates,
    onNext,
    recordValues,
    processingFlowTemplate,
    setProcessingFlowTemplate,
    processingFlowTemplates,
    templateValues,
    setTemplateValues,
    buttonText = 'Next',
}: {
    platform: 'romr' | 'clickerbooth' | null;
    experience: DefinitionTemplate | null;
    setExperience: (experience: DefinitionTemplate | null) => void;
    experienceTemplates: DefinitionTemplate[];
    onNext: () => void;
    recordValues: (
        values: Record<
            string,
            string | Date | boolean | DefinitionTemplate | null | number
        >,
    ) => void;
    processingFlowTemplate: DefinitionTemplate | null;
    setProcessingFlowTemplate: (
        processingFlowTemplate: DefinitionTemplate | null,
    ) => void;
    processingFlowTemplates: DefinitionTemplate[];
    templateValues: Record<string, any>;
    setTemplateValues: (templateValues: Record<string, any>) => void;
    buttonText?: string;
}) {
    const onValueChange = useCallback((key: string, value: any) => {
        console.log('key', key);
        console.log('value', value);
        setTemplateValues((prevValues: Record<string, any>) => ({
            ...prevValues,
            [key]: value,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (experience === null && processingFlowTemplate === null) {
            return;
        }
        if (platform === 'romr') {
            const definition = processFlowTemplate(
                templateValues,
                processingFlowTemplate as unknown as DefinitionTemplate,
            );
            recordValues({
                processingFlow: definition,
            });
        } else if (platform === 'clickerbooth') {
            const definition = processExperienceTemplate(
                templateValues,
                experience as unknown as DefinitionTemplate,
            );
            recordValues({
                eventTemplate: definition,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateValues, platform, experience, processingFlowTemplate]);
    console.log('Platform', platform);
    console.log('Experience', experience);
    console.log('Processing Flow Template', processingFlowTemplate);
    console.log('Experience Templates', experienceTemplates);

    return (
        <>
            <section
                className={cn(
                    'transition-all duration-300 transition-delay-200',
                    platform !== 'clickerbooth' &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                    experience !== null &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <div className="space-y-1">
                    <Subheading>Choose Experience Type</Subheading>
                </div>
            </section>
            <section
                className={cn(
                    'grid gap-x-8 gap-y-6 sm:grid-cols-2 py-10 transition-all duration-300 transition-delay-200',
                    platform !== 'clickerbooth' &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                    experience !== null &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <div className="space-y-1 col-span-2">
                    <TemplateGrid
                        items={experienceTemplates}
                        onSelect={setExperience}
                    />
                </div>
            </section>

            <section
                className={cn(
                    'transition-all duration-300 transition-delay-200',
                    experience === null &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <div className="space-y-1">
                    <Subheading>Customize the Experience</Subheading>
                </div>
            </section>

            <section
                className={cn(
                    'transition-all duration-300 transition-delay-200',
                    experience === null &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <ExperienceCustomizer
                    experience={experience}
                    values={templateValues}
                    onValueChange={onValueChange}
                />
            </section>

            <section
                className={cn(
                    'transition-all duration-300 transition-delay-200',
                    platform !== 'romr' &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <div className="space-y-1">
                    <Subheading>Configure the Output</Subheading>
                </div>
            </section>
            <section
                className={cn(
                    'grid gap-x-8 gap-y-6 sm:grid-cols-2 py-10 transition-all duration-300 transition-delay-200',
                    platform !== 'romr' &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                    processingFlowTemplate !== null &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <div className="space-y-1 col-span-2">
                    <TemplateGrid
                        items={processingFlowTemplates}
                        onSelect={setProcessingFlowTemplate}
                    />
                </div>
            </section>

            <section
                className={cn(
                    'transition-all duration-300 transition-delay-200',
                    processingFlowTemplate === null &&
                        'h-0 m-0 p-0 opacity-0 overflow-hidden',
                )}
            >
                <ExperienceCustomizer
                    experience={processingFlowTemplate}
                    values={templateValues}
                    onValueChange={onValueChange}
                />
            </section>

            <Divider className="my-10" soft />
            <div className="my-10" />
            {experience || processingFlowTemplate ? (
                <div className="flex justify-end gap-4 select-none">
                    {/* @ts-ignore */}
                    <Button
                        className="mt-4 dark-button disabled:opacity-50 select-none"
                        onClick={() => {
                            onNext();
                        }}
                    >
                        {buttonText}
                    </Button>
                </div>
            ) : null}
        </>
    );
}
